"use client";

import { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Mail,
  ShieldCheck,
} from "lucide-react";

import MarketingConsent from "@/components/forms/MarketingConsent";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { trackContactForm } from "@/lib/analytics";
import {
  PRIVACY_NOTICE_PATH,
  appendMarketingConsent,
  withMarketingConsentLine,
} from "@/lib/marketing-consent";
import {
  SEO_FREE_WEBSITE,
  SEO_PLANS,
  rm,
  SEO_PLANS_SGD_MONTHLY,
  sgd,
} from "@/lib/seo-pricing";
import { whatsappHref } from "@/lib/whatsapp";

const WHATSAPP_NUMBER = "601159246128";

const PACKAGE_OPTIONS = [
  { value: "essential", label: `${SEO_PLANS.essential.name} — ${rm(SEO_PLANS.essential.monthly)}/month` },
  { value: "growth", label: `${SEO_PLANS.growth.name} — ${rm(SEO_PLANS.growth.monthly)}/month` },
  { value: "managed", label: `${SEO_PLANS.managed.name} — from ${rm(SEO_PLANS.managed.monthly)}/month` },
  { value: "bundle", label: `Free website with Growth or Managed (${SEO_FREE_WEBSITE.commitmentMonths}-month plan)` },
  { value: "unsure", label: "Free AI visibility check first — then recommend a package" },
] as const;

// Singapore (/singapore/ai-seo/): SGD prices from SEO_PLANS_SGD_MONTHLY, same
// terms as Malaysia (approved 2026-10-04).
const SG_PACKAGE_OPTIONS = [
  { value: "essential", label: `${SEO_PLANS.essential.name} — ${sgd(SEO_PLANS_SGD_MONTHLY.essential)}/month` },
  { value: "growth", label: `${SEO_PLANS.growth.name} — ${sgd(SEO_PLANS_SGD_MONTHLY.growth)}/month` },
  { value: "managed", label: `${SEO_PLANS.managed.name} — from ${sgd(SEO_PLANS_SGD_MONTHLY.managed)}/month` },
  { value: "bundle", label: `Free website with Growth or Managed (${SEO_FREE_WEBSITE.commitmentMonths}-month plan)` },
  { value: "unsure", label: "Free AI visibility check first — then recommend a plan" },
] as const;

/**
 * Visible (translated) label for a package option. The English `label` above is
 * still what goes into the enquiry email. Prices are spliced into {price} so a
 * price change never orphans the ms/zh translations.
 */
function packageDisplayLabel(value: string, isSg: boolean, locale: Locale): string {
  const price = (myr: number, sgdAmount: number) => (isSg ? sgd(sgdAmount) : rm(myr));
  switch (value) {
    case "essential":
      return `${SEO_PLANS.essential.name} — ${t(locale, "{price}/month").replace("{price}", price(SEO_PLANS.essential.monthly, SEO_PLANS_SGD_MONTHLY.essential))}`;
    case "growth":
      return `${SEO_PLANS.growth.name} — ${t(locale, "{price}/month").replace("{price}", price(SEO_PLANS.growth.monthly, SEO_PLANS_SGD_MONTHLY.growth))}`;
    case "managed":
      return `${SEO_PLANS.managed.name} — ${t(locale, "from {price}/month").replace("{price}", price(SEO_PLANS.managed.monthly, SEO_PLANS_SGD_MONTHLY.managed))}`;
    case "bundle":
      return t(locale, "Free website with Growth or Managed ({months}-month plan)").replace(
        "{months}",
        String(SEO_FREE_WEBSITE.commitmentMonths),
      );
    default:
      return t(
        locale,
        isSg
          ? "Free AI visibility check first — then recommend a plan"
          : "Free AI visibility check first — then recommend a package",
      );
  }
}

/** Which market the form serves. "my" is the Malaysia SEO page (default). */
export type SeoEnquiryMarket = "my" | "sg";

const WEBSITE_OPTIONS = [
  { value: "existing", label: "I have an existing website" },
  { value: "new", label: "I need a new website" },
] as const;

const VENDOR_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "not_sure", label: "Not sure" },
] as const;

const SPEND_OPTIONS = [
  "Below RM500/month",
  "RM500–RM999/month",
  "RM1,000–RM1,999/month",
  "RM2,000+/month",
  "Prefer not to say",
] as const;

const PROBLEM_OPTIONS = [
  {
    value: "not_in_ai_answers",
    label: "ChatGPT or Google AI answers do not mention us",
  },
  {
    value: "not_enough_enquiries",
    label: "Our website is not generating enough enquiries",
  },
  {
    value: "not_found_on_google",
    label: "Customers cannot find us on Google",
  },
  {
    value: "declining_performance",
    label: "Our rankings or search traffic are declining",
  },
  {
    value: "keyword_uncertainty",
    label: "We are unsure which keywords to target",
  },
  {
    value: "technical_issues",
    label: "Our website has technical SEO problems",
  },
  {
    value: "vendor_results",
    label: "Our current SEO vendor is not producing clear results",
  },
  {
    value: "no_internal_capacity",
    label: "We do not have the time or SEO expertise internally",
  },
  {
    value: "new_seo_website",
    label: "We need a new website built for SEO",
  },
  { value: "other", label: "Other" },
] as const;

type Status = "idle" | "loading" | "done" | "error";

type FormState = {
  name: string;
  email: string;
  company: string;
  websiteStatus: "existing" | "new";
  websiteUrl: string;
  packageInterest: "essential" | "growth" | "managed" | "bundle" | "unsure";
  vendorStatus: "yes" | "no" | "not_sure";
  vendorSpend: string;
  problems: string[];
  details: string;
  // Honeypot. Hidden from people and from screen readers; bots fill every field
  // they find, so a non-empty value here means the submission is not human.
  website2: string;
};

const initialForm: FormState = {
  website2: "",
  name: "",
  email: "",
  company: "",
  websiteStatus: "existing",
  websiteUrl: "",
  packageInterest: "unsure",
  vendorStatus: "no",
  vendorSpend: "",
  problems: [],
  details: "",
};

function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string,
) {
  return options.find((option) => option.value === value)?.label ?? value;
}

export default function SeoEnquiryForm({
  market = "my",
  locale = defaultLocale,
  source,
}: {
  market?: SeoEnquiryMarket;
  /** Visible-label language. The enquiry email stays in English. */
  locale?: Locale;
  /** Overrides the Web3Forms `source` field, e.g. "ai-visibility-check". */
  source?: string;
} = {}) {
  const isSg = market === "sg";
  const packageOptions: readonly { value: string; label: string }[] = isSg
    ? SG_PACKAGE_OPTIONS
    : PACKAGE_OPTIONS;
  const [status, setStatus] = useState<Status>("idle");
  const [form, setForm] = useState<FormState>(initialForm);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    const packageParam = new URLSearchParams(window.location.search).get(
      "package",
    );
    const options = market === "sg" ? SG_PACKAGE_OPTIONS : PACKAGE_OPTIONS;
    const validPackage = (options as readonly { value: string }[]).some(
      (option) => option.value === packageParam,
    );

    if (packageParam && validPackage) {
      setForm((current) => ({
        ...current,
        packageInterest: packageParam as FormState["packageInterest"],
        // The bundle is a free website: someone who chose it from the web design
        // page needs a new site, so do not preselect "existing website".
        ...(packageParam === "bundle" ? { websiteStatus: "new" as FormState["websiteStatus"] } : {}),
      }));
    }
  }, [market]);

  const setField = <Key extends keyof FormState>(
    key: Key,
    value: FormState[Key],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleProblem = (problem: string) => {
    setForm((current) => ({
      ...current,
      problems: current.problems.includes(problem)
        ? current.problems.filter((item) => item !== problem)
        : [...current.problems, problem],
    }));
  };

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Honeypot tripped — show the normal success state rather than an error, so a
    // bot gets no signal about why nothing arrived. Nothing is sent or tracked.
    if (form.website2.trim() !== "") {
      setStatus("done");
      return;
    }

    const accessKey = process.env.NEXT_PUBLIC_WEB3FORMS_KEY;
    if (!accessKey || accessKey === "your_actual_web3forms_key_here") {
      setStatus("error");
      trackContactForm(
        {
          success: false,
          formName: "seo_agent_enquiry",
          fieldsFilled: 0,
        },
        {
          website_status: form.websiteStatus,
          package_interest: form.packageInterest,
          vendor_status: form.vendorStatus,
          problem_count: form.problems.length,
          market,
        },
      );
      return;
    }

    setStatus("loading");

    const packageLabel = optionLabel(
      packageOptions,
      form.packageInterest,
    );
    const websiteStatusLabel = optionLabel(
      WEBSITE_OPTIONS,
      form.websiteStatus,
    );
    const vendorStatusLabel = optionLabel(VENDOR_OPTIONS, form.vendorStatus);
    const problemLabels = form.problems.map((problem) =>
      optionLabel(PROBLEM_OPTIONS, problem),
    );
    const message = [
      `SEO Agent enquiry from ${form.company}${isSg ? " (Singapore)" : ""}`,
      `Website status: ${websiteStatusLabel}`,
      `Website: ${form.websiteUrl || "Not provided"}`,
      `Package interest: ${packageLabel}`,
      `Existing SEO vendor: ${vendorStatusLabel}`,
      `Current monthly SEO spend: ${form.vendorSpend || "Not provided"}`,
      `Main problems:\n- ${problemLabels.join("\n- ") || "Not provided"}`,
      `Additional details: ${form.details || "Not provided"}`,
    ].join("\n\n");

    try {
      const body = new FormData();
      body.append("access_key", accessKey);
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("company", form.company);
      body.append("request_type", "SEO Agent Enquiry");
      body.append(
        "source",
        source ?? (isSg ? "singapore-ai-seo" : "seo-services-malaysia"),
      );
      body.append(
        "subject",
        `${isSg ? "[SG] " : ""}SEO Agent enquiry — ${form.company} — ${packageLabel}`,
      );
      body.append("from_name", "Anchor Sprint Website");
      body.append("website_status", websiteStatusLabel);
      body.append("website_url", form.websiteUrl);
      body.append("package_interest", packageLabel);
      body.append("has_existing_seo_vendor", vendorStatusLabel);
      body.append("current_monthly_seo_spend", form.vendorSpend);
      body.append("seo_problems", problemLabels.join(", "));
      body.append("additional_details", form.details);
      body.append("message", withMarketingConsentLine(message, marketingConsent));
      appendMarketingConsent(body, marketingConsent, "en");

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body,
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Web3Forms submission failed");
      }

      trackContactForm(
        {
          success: true,
          formName: "seo_agent_enquiry",
          fieldsFilled: Object.values(form).filter((value) =>
            Array.isArray(value) ? value.length > 0 : Boolean(value),
          ).length,
        },
        {
          website_status: form.websiteStatus,
          package_interest: form.packageInterest,
          vendor_status: form.vendorStatus,
          problem_count: form.problems.length,
          market,
        },
      );
      setStatus("done");
    } catch {
      trackContactForm(
        {
          success: false,
          formName: "seo_agent_enquiry",
          fieldsFilled: Object.values(form).filter((value) =>
            Array.isArray(value) ? value.length > 0 : Boolean(value),
          ).length,
        },
        {
          website_status: form.websiteStatus,
          package_interest: form.packageInterest,
          vendor_status: form.vendorStatus,
          problem_count: form.problems.length,
          market,
        },
      );
      setStatus("error");
    }
  }

  if (status === "done") {
    return (
      <div
        className="rounded-3xl border border-success/30 bg-success/5 p-8 text-center sm:p-10"
        role="status"
        aria-live="polite"
      >
        <CheckCircle2
          className="mx-auto h-12 w-12 text-success"
          aria-hidden
        />
        <h3 className="mt-5 font-display text-2xl font-bold text-neutral-emphasis">
          {t(locale, "Your SEO enquiry is with our team")}
        </h3>
        <p className="mx-auto mt-3 max-w-xl leading-relaxed text-neutral-muted">
          {t(
            locale,
            "We will run your AI visibility check, review your website and priorities, then reply within one business day with what we found and the most sensible next step.",
          )}
        </p>
      </div>
    );
  }

  const fieldClassName =
    "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-neutral-emphasis outline-none transition placeholder:text-neutral-muted/60 focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClassName =
    "mb-2 block text-sm font-semibold text-neutral-emphasis";

  return (
    <form onSubmit={handleSubmit} className="space-y-7" noValidate={false}>
      {/* Honeypot — off-screen rather than display:none, because some bots skip
          hidden inputs. aria-hidden and tabIndex keep it away from real users. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        <label htmlFor="seo-website2">Leave this field empty</label>
        <input
          id="seo-website2"
          type="text"
          name="website2"
          autoComplete="off"
          tabIndex={-1}
          value={form.website2}
          onChange={(event) => setField("website2", event.target.value)}
        />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClassName} htmlFor="seo-name">
            {t(locale, "Your name")}
          </label>
          <input
            id="seo-name"
            type="text"
            autoComplete="name"
            required
            className={fieldClassName}
            value={form.name}
            onChange={(event) => setField("name", event.target.value)}
          />
        </div>
        <div>
          <label className={labelClassName} htmlFor="seo-email">
            {t(locale, "Work email")}
          </label>
          <input
            id="seo-email"
            type="email"
            autoComplete="email"
            required
            className={fieldClassName}
            value={form.email}
            onChange={(event) => setField("email", event.target.value)}
          />
        </div>
      </div>

      <div>
        <label className={labelClassName} htmlFor="seo-company">
          {t(locale, "Company")}
        </label>
        <input
          id="seo-company"
          type="text"
          autoComplete="organization"
          required
          className={fieldClassName}
          value={form.company}
          onChange={(event) => setField("company", event.target.value)}
        />
      </div>

      <fieldset>
        <legend className={labelClassName}>{t(locale, "What do you need?")}</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {WEBSITE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 text-sm transition ${
                form.websiteStatus === option.value
                  ? "border-primary bg-primary/5 text-neutral-emphasis"
                  : "border-border text-neutral-muted hover:border-primary/45"
              }`}
            >
              <input
                type="radio"
                name="website-status"
                value={option.value}
                checked={form.websiteStatus === option.value}
                onChange={() =>
                  setField(
                    "websiteStatus",
                    option.value as FormState["websiteStatus"],
                  )
                }
                className="h-4 w-4 accent-primary"
              />
              <span className="font-medium">{t(locale, option.label)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label className={labelClassName} htmlFor="seo-website">
          {form.websiteStatus === "existing"
            ? t(locale, "Existing website URL")
            : t(locale, "Current website URL (optional)")}
        </label>
        <input
          id="seo-website"
          type="url"
          inputMode="url"
          autoComplete="url"
          required={form.websiteStatus === "existing"}
          placeholder="https://yourcompany.com"
          className={fieldClassName}
          value={form.websiteUrl}
          onChange={(event) => setField("websiteUrl", event.target.value)}
        />
        <p className="mt-2 text-xs text-neutral-muted">
          {t(locale, "Include https:// so we can validate the address correctly.")}
        </p>
      </div>

      <div>
        <label className={labelClassName} htmlFor="seo-package">
          {t(locale, "Which package interests you?")}
        </label>
        <select
          id="seo-package"
          required
          className={fieldClassName}
          value={form.packageInterest}
          onChange={(event) =>
            setField(
              "packageInterest",
              event.target.value as FormState["packageInterest"],
            )
          }
        >
          {packageOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {packageDisplayLabel(option.value, isSg, locale)}
            </option>
          ))}
        </select>
      </div>

      <fieldset>
        <legend className={labelClassName}>
          {t(locale, "Do you currently use an SEO vendor?")}
        </legend>
        <div className="flex flex-wrap gap-3">
          {VENDOR_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition ${
                form.vendorStatus === option.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-neutral-muted hover:border-primary/45"
              }`}
            >
              <input
                type="radio"
                name="vendor-status"
                value={option.value}
                checked={form.vendorStatus === option.value}
                onChange={() => {
                  setField(
                    "vendorStatus",
                    option.value as FormState["vendorStatus"],
                  );
                  if (option.value !== "yes") {
                    setField("vendorSpend", "");
                  }
                }}
                className="sr-only"
              />
              {t(locale, option.label)}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Spend bands are in RM, so the question is Malaysia-only. */}
      {!isSg && form.vendorStatus === "yes" && (
        <div>
          <label className={labelClassName} htmlFor="seo-vendor-spend">
            {t(locale, "What do you currently spend on SEO?")}{" "}
            <span className="font-normal text-neutral-muted">{t(locale, "(optional)")}</span>
          </label>
          <select
            id="seo-vendor-spend"
            className={fieldClassName}
            value={form.vendorSpend}
            onChange={(event) => setField("vendorSpend", event.target.value)}
          >
            <option value="">{t(locale, "Select a monthly range")}</option>
            {SPEND_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(locale, option)}
              </option>
            ))}
          </select>
        </div>
      )}

      <fieldset id="seo-problems">
        <legend className={labelClassName}>
          {t(locale, "What problems are you facing?")}{" "}
          <span className="font-normal text-neutral-muted">{t(locale, "(optional)")}</span>
        </legend>
        <p className="mb-3 text-xs text-neutral-muted">
          {t(locale, "Choose any that apply.")}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {PROBLEM_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-4 text-sm text-neutral-muted transition hover:border-primary/45"
            >
              <input
                type="checkbox"
                value={option.value}
                checked={form.problems.includes(option.value)}
                onChange={() => toggleProblem(option.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
              />
              <span>{t(locale, option.label)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label className={labelClassName} htmlFor="seo-details">
          {t(locale, "Tell us what is not working or what you want to improve")}
          {!form.problems.includes("other") && (
            <span className="font-normal text-neutral-muted"> {t(locale, "(optional)")}</span>
          )}
        </label>
        <textarea
          id="seo-details"
          rows={4}
          required={form.problems.includes("other")}
          className={fieldClassName}
          placeholder={t(
            locale,
            "For example: we receive traffic but very few enquiries, and our current reports do not explain what work was completed.",
          )}
          value={form.details}
          onChange={(event) => setField("details", event.target.value)}
        />
      </div>

      {status === "error" && (
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-neutral-emphasis"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <AlertCircle
              className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
              aria-hidden
            />
            <p>
              {t(
                locale,
                "We could not send your enquiry. Your answers are still here, so you can try again or",
              )}{" "}
              <a
                href={whatsappHref(
                  "Hi Anchor Sprint — I would like to discuss an AI SEO plan.",
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-track-event="contact_whatsapp_click"
                data-track-location="seo_enquiry_form_error"
                data-track-phone={WHATSAPP_NUMBER}
                className="font-semibold text-primary underline underline-offset-2"
              >
                {t(locale, "contact us on WhatsApp")}
              </a>
              .
            </p>
          </div>
        </div>
      )}

      <MarketingConsent
        id="seo-marketing-consent"
        checked={marketingConsent}
        onChange={setMarketingConsent}
        locale={locale}
      />

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {status === "loading"
            ? t(locale, "Sending your enquiry…")
            : t(locale, "Get my SEO opportunity review")}
          {status !== "loading" && (
            <ArrowRight className="h-4 w-4" aria-hidden />
          )}
        </button>
        <span
          className="text-sm text-neutral-muted"
          role="status"
          aria-live="polite"
        >
          {status === "loading"
            ? t(locale, "Securely sending…")
            : t(locale, "Reply within one business day")}
        </span>
      </div>

      <div className="grid gap-3 border-t border-border pt-5 text-xs leading-relaxed text-neutral-muted sm:grid-cols-2">
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          {/* Links to the PDPA privacy notice at /privacy-policy/, localised to the
              form's locale (en on the AI SEO pages, ms/zh on the AI visibility
              check). The old "only to reply" wording was
              replaced because marketing email is now possible when the consent
              box above is ticked. */}
          <span>
            {t(
              locale,
              "We use this information to assess and reply to your enquiry, and send marketing emails only if you tick the box above. Read our",
            )}{" "}
            <a
              href={getLocalizedPathname(PRIVACY_NOTICE_PATH, locale)}
              className="font-semibold text-primary underline underline-offset-2"
            >
              {t(locale, "Privacy Notice")}
            </a>
            .
          </span>
        </p>
        <p className="flex items-start gap-2">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          {t(
            locale,
            "No expensive SEO subscription is required. We provide the research tools, expert insight, and recommended work.",
          )}
        </p>
      </div>
    </form>
  );
}
